import { storableError } from '../../util/errors';
import { parse } from '../../util/urlHelpers';
import { getAllTransitionsForEveryProcess } from '../../transactions/transaction';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { createImageVariantConfig } from '../../util/sdkLoader';
import { isPractitionerOnboardingProcess } from '../../util/practitionerOnboardingTransaction';
import {
  isCampaignInboxListing,
  isPractitionerCampaignProcess,
} from '../../util/practitionerCampaign';
import { ASPECT_HEIGHT_LISTING, ASPECT_WIDTH_LISTING } from '../../config/configLayout';
import { denormalisedResponseEntities } from '../../util/data';
import {
  INBOX_API_FETCH_PAGE_SIZE,
  INBOX_LISTING_FILTER_PAGE_SIZE,
  INBOX_PAGE_SIZE,
  accumulateFilteredInboxPage,
  buildInboxPaginationMeta,
  buildInboxTransactionApiFilters,
  getValidWhenFilter,
  matchesInboxListingFilter,
  matchesInboxWhenFilter,
  parseInboxPage,
  parseListingIds,
} from './inboxPagination';
import { getStateData } from './InboxPage.stateData';

// ================ Action types ================ //

export const FETCH_ORDERS_OR_SALES_REQUEST = 'app/InboxPage/FETCH_ORDERS_OR_SALES_REQUEST';
export const FETCH_ORDERS_OR_SALES_SUCCESS = 'app/InboxPage/FETCH_ORDERS_OR_SALES_SUCCESS';
export const FETCH_ORDERS_OR_SALES_ERROR = 'app/InboxPage/FETCH_ORDERS_OR_SALES_ERROR';

export const FETCH_FILTER_LISTINGS_REQUEST = 'app/InboxPage/FETCH_FILTER_LISTINGS_REQUEST';
export const FETCH_FILTER_LISTINGS_SUCCESS = 'app/InboxPage/FETCH_FILTER_LISTINGS_SUCCESS';
export const FETCH_FILTER_LISTINGS_ERROR = 'app/InboxPage/FETCH_FILTER_LISTINGS_ERROR';
export const SET_INBOX_TAB = 'app/InboxPage/SET_INBOX_TAB';

// ================ Reducer ================ //

const entityRefs = entities =>
  entities.map(entity => ({
    id: entity.id,
    type: entity.type,
  }));

const initialState = {
  fetchInProgress: false,
  fetchOrdersOrSalesError: null,
  pagination: null,
  transactionRefs: [],
  inboxTab: null,
  filterListings: [],
  filterListingsPagination: null,
  filterListingsFetchInProgress: false,
  filterListingsFetchError: null,
  filterListingsKeywords: '',
};

export default function inboxPageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case SET_INBOX_TAB:
      return { ...state, inboxTab: payload };

    case FETCH_ORDERS_OR_SALES_REQUEST:
      return { ...state, fetchInProgress: true, fetchOrdersOrSalesError: null };
    case FETCH_ORDERS_OR_SALES_SUCCESS: {
      // Preserve API / accumulate order (upcoming uses bookingStart sort).
      const transactions = payload.data.data || [];
      return {
        ...state,
        fetchInProgress: false,
        transactionRefs: entityRefs(transactions),
        pagination: payload.data.meta,
      };
    }
    case FETCH_ORDERS_OR_SALES_ERROR:
      console.error(payload); // eslint-disable-line
      return { ...state, fetchInProgress: false, fetchOrdersOrSalesError: payload };

    case FETCH_FILTER_LISTINGS_REQUEST: {
      const nextKeywords = payload?.keywords || '';
      const keywordsChanged = nextKeywords !== (state.filterListingsKeywords || '');
      return {
        ...state,
        filterListingsFetchInProgress: true,
        filterListingsFetchError: null,
        ...(payload?.append || !keywordsChanged ? {} : { filterListings: [] }),
        filterListingsKeywords: nextKeywords,
      };
    }
    case FETCH_FILTER_LISTINGS_SUCCESS: {
      const nextListings = payload.append
        ? [...state.filterListings, ...payload.listings]
        : payload.listings;
      const seen = new Set();
      const deduped = nextListings.filter(listing => {
        const id = listing?.id?.uuid;
        if (!id || seen.has(id)) {
          return false;
        }
        seen.add(id);
        return true;
      });
      return {
        ...state,
        filterListingsFetchInProgress: false,
        filterListings: deduped,
        filterListingsPagination: payload.pagination,
        filterListingsKeywords: payload.keywords || '',
      };
    }
    case FETCH_FILTER_LISTINGS_ERROR:
      return {
        ...state,
        filterListingsFetchInProgress: false,
        filterListingsFetchError: payload,
      };

    default:
      return state;
  }
}

// ================ Action creators ================ //

const setInboxTab = tab => ({ type: SET_INBOX_TAB, payload: tab });

const fetchOrdersOrSalesRequest = () => ({ type: FETCH_ORDERS_OR_SALES_REQUEST });
const fetchOrdersOrSalesSuccess = response => ({
  type: FETCH_ORDERS_OR_SALES_SUCCESS,
  payload: response,
});
const fetchOrdersOrSalesError = e => ({
  type: FETCH_ORDERS_OR_SALES_ERROR,
  error: true,
  payload: e,
});

const fetchFilterListingsRequest = ({ append, keywords }) => ({
  type: FETCH_FILTER_LISTINGS_REQUEST,
  payload: { append, keywords },
});
const fetchFilterListingsSuccess = ({ listings, pagination, keywords, append }) => ({
  type: FETCH_FILTER_LISTINGS_SUCCESS,
  payload: { listings, pagination, keywords, append },
});
const fetchFilterListingsError = e => ({
  type: FETCH_FILTER_LISTINGS_ERROR,
  error: true,
  payload: e,
});

// ================ Thunks ================ //

const aspectRatio = ASPECT_HEIGHT_LISTING / ASPECT_WIDTH_LISTING;

const buildTransactionQueryParams = ({ onlyFilter, apiFilters }) => ({
  only: onlyFilter,
  lastTransitions: getAllTransitionsForEveryProcess(),
  include: [
    'listing',
    'listing.images',
    'provider',
    'provider.profileImage',
    'customer',
    'customer.profileImage',
    'booking',
    'images',
  ],
  'fields.transaction': [
    'processName',
    'lastTransition',
    'lastTransitionedAt',
    'createdAt',
    'transitions',
    'payinTotal',
    'payoutTotal',
    'lineItems',
    'protectedData',
  ],
  'fields.listing': [
    'title',
    'availabilityPlan',
    'publicData.listingType',
    'publicData.transactionProcessAlias',
    'publicData.unitType',
    'publicData.accessMode',
    'publicData.subscription',
  ],
  'fields.user': ['profile.displayName', 'profile.abbreviatedName'],
  'fields.image': [
    'variants.square-small',
    'variants.square-small2x',
    'variants.listing-card',
    'variants.listing-card-2x',
  ],
  ...createImageVariantConfig('listing-card', 400, aspectRatio),
  ...createImageVariantConfig('listing-card-2x', 800, aspectRatio),
  ...apiFilters,
});

const txMatchesInboxFilters = ({ tx, when, listingIds, transactionRole }) => {
  if (
    isPractitionerOnboardingProcess(tx?.attributes?.processName) ||
    isPractitionerCampaignProcess(tx?.attributes?.processName)
  ) {
    return false;
  }

  let isFinal = false;
  try {
    const stateData = getStateData({ transaction: tx, transactionRole });
    isFinal = !!stateData?.isFinal;
  } catch (e) {
    // If state data cannot be resolved, still allow when/listing matching.
  }

  return matchesInboxWhenFilter({ tx, when, isFinal }) && matchesInboxListingFilter(tx, listingIds);
};

/**
 * Load own listings (sales) or discover listings from order txs (orders)
 * for the inbox listing filter dropdown — 20 at a time.
 */
export const fetchInboxFilterListings = ({ tab, page = 1, keywords = '', append = false } = {}) => (
  dispatch,
  getState,
  sdk
) => {
  const trimmedKeywords = typeof keywords === 'string' ? keywords.trim() : '';
  dispatch(fetchFilterListingsRequest({ append, keywords: trimmedKeywords }));

  if (tab === 'sales') {
    const queryParams = {
      page,
      perPage: INBOX_LISTING_FILTER_PAGE_SIZE,
      'fields.listing': ['title', 'state'],
      ...(trimmedKeywords ? { keywords: trimmedKeywords } : {}),
    };

    return sdk.ownListings
      .query(queryParams)
      .then(response => {
        const listings = denormalisedResponseEntities(response).filter(
          listing => !isCampaignInboxListing(listing)
        );
        const meta = response?.data?.meta || {};
        dispatch(
          fetchFilterListingsSuccess({
            listings,
            pagination: meta,
            keywords: trimmedKeywords,
            append,
          })
        );
        return response;
      })
      .catch(e => {
        dispatch(fetchFilterListingsError(storableError(e)));
        throw e;
      });
  }

  // Orders tab: discover relevant listings from the customer's transactions.
  // Continue from the last scanned transactions API page when appending.
  const matchesTitle = listing => {
    if (!trimmedKeywords) {
      return true;
    }
    const title = listing?.attributes?.title || '';
    return title.toLowerCase().includes(trimmedKeywords.toLowerCase());
  };

  const alreadyLoadedIds = new Set(
    (append ? getState().InboxPage?.filterListings || [] : []).map(l => l?.id?.uuid).filter(Boolean)
  );
  const queryTxPage = apiPage =>
    sdk.transactions.query({
      only: 'order',
      lastTransitions: getAllTransitionsForEveryProcess(),
      include: ['listing'],
      'fields.transaction': ['processName', 'lastTransition'],
      'fields.listing': ['title'],
      page: apiPage,
      perPage: INBOX_LISTING_FILTER_PAGE_SIZE,
    });

  const need = INBOX_LISTING_FILTER_PAGE_SIZE + 1;
  const batch = [];
  const previousDiscoveryPage = append
    ? getState().InboxPage?.filterListingsPagination?.discoveryApiPage || 0
    : 0;
  let apiPage = previousDiscoveryPage + 1;
  let apiTotalPages = 1;
  let fetched = 0;
  const maxPages = 25;

  const walk = () => {
    if (batch.length >= need || apiPage > apiTotalPages || fetched >= maxPages) {
      return Promise.resolve();
    }
    return queryTxPage(apiPage).then(response => {
      fetched += 1;
      apiTotalPages = Math.max(1, Number(response?.data?.meta?.totalPages) || 1);
      const txs = denormalisedResponseEntities(response);
      txs.forEach(tx => {
        const listing = tx?.listing;
        const id = listing?.id?.uuid;
        if (
          id &&
          matchesTitle(listing) &&
          !alreadyLoadedIds.has(id) &&
          !batch.some(l => l?.id?.uuid === id)
        ) {
          batch.push(listing);
        }
      });
      apiPage += 1;
      return walk();
    });
  };

  return walk()
    .then(() => {
      const pageSlice = batch.slice(0, INBOX_LISTING_FILTER_PAGE_SIZE);
      const hasMore = batch.length > INBOX_LISTING_FILTER_PAGE_SIZE || apiPage <= apiTotalPages;
      dispatch(
        fetchFilterListingsSuccess({
          listings: pageSlice,
          pagination: {
            page,
            perPage: INBOX_LISTING_FILTER_PAGE_SIZE,
            totalPages: hasMore ? page + 1 : Math.max(1, page),
            totalItems: hasMore
              ? page * INBOX_LISTING_FILTER_PAGE_SIZE + 1
              : (page - 1) * INBOX_LISTING_FILTER_PAGE_SIZE + pageSlice.length,
            discoveryApiPage: apiPage - 1,
          },
          keywords: trimmedKeywords,
          append,
        })
      );
    })
    .catch(e => {
      dispatch(fetchFilterListingsError(storableError(e)));
      throw e;
    });
};

export const loadMoreInboxFilterListings = () => (dispatch, getState) => {
  const {
    inboxTab,
    filterListingsFetchInProgress,
    filterListingsPagination,
    filterListingsKeywords,
  } = getState().InboxPage;

  if (filterListingsFetchInProgress || !inboxTab) {
    return Promise.resolve();
  }

  const page = filterListingsPagination?.page || 1;
  const totalPages = filterListingsPagination?.totalPages || 1;
  if (page >= totalPages) {
    return Promise.resolve();
  }

  return dispatch(
    fetchInboxFilterListings({
      tab: inboxTab,
      page: page + 1,
      keywords: filterListingsKeywords || '',
      append: true,
    })
  );
};

export const searchInboxFilterListings = ({ tab, keywords }) => dispatch =>
  dispatch(
    fetchInboxFilterListings({
      tab,
      page: 1,
      keywords,
      append: false,
    })
  );

export const loadData = (params, search) => (dispatch, getState, sdk) => {
  const { tab } = params;
  const queryParams = parse(search);

  const onlyFilterValues = {
    orders: 'order',
    sales: 'sale',
  };

  const onlyFilter = onlyFilterValues[tab];
  if (!onlyFilter) {
    return Promise.reject(new Error(`Invalid tab for InboxPage: ${tab}`));
  }

  const when = getValidWhenFilter(queryParams.when);
  const listingIds = parseListingIds(queryParams.listings);
  const page = parseInboxPage(queryParams.page);
  const transactionRole = tab === 'orders' ? 'customer' : 'provider';
  const apiFilters = buildInboxTransactionApiFilters({ when, listingIds });

  dispatch(setInboxTab(tab));
  dispatch(fetchOrdersOrSalesRequest());

  const baseQueryParams = buildTransactionQueryParams({ onlyFilter, apiFilters });

  // Fetch raw SDK pages, denormalise for filter matching, keep raw entities for the store.
  const queryPageDenormalised = apiPage =>
    sdk.transactions
      .query({
        ...baseQueryParams,
        page: apiPage,
        perPage: INBOX_API_FETCH_PAGE_SIZE,
      })
      .then(response => {
        const rawData = response?.data?.data || [];
        const denormalised = denormalisedResponseEntities(response);
        const withRaw = denormalised.map((tx, index) => ({
          ...tx,
          __raw: rawData[index],
        }));
        return {
          ...response,
          data: {
            ...response.data,
            data: withRaw,
            included: response?.data?.included || [],
          },
        };
      });

  const matches = tx =>
    txMatchesInboxFilters({
      tx,
      when,
      listingIds,
      transactionRole,
    });

  const filterListingsPromise = dispatch(
    fetchInboxFilterListings({
      tab,
      page: 1,
      keywords: '',
      append: false,
    })
  ).catch(() => null);

  return Promise.all([
    accumulateFilteredInboxPage({
      queryPage: queryPageDenormalised,
      matches,
      uiPage: page,
      pageSize: INBOX_PAGE_SIZE,
    }),
    filterListingsPromise,
  ])
    .then(([pageResult]) => {
      const { pageItems, hasMore, included } = pageResult;
      const meta = buildInboxPaginationMeta({
        page,
        itemCountOnPage: pageItems.length,
        hasMore,
        pageSize: INBOX_PAGE_SIZE,
      });

      const rawPageItems = pageItems.map(tx => tx.__raw).filter(Boolean);
      const response = {
        data: {
          data: rawPageItems,
          included,
          meta,
        },
      };

      dispatch(addMarketplaceEntities(response));
      dispatch(fetchOrdersOrSalesSuccess(response));
      return response;
    })
    .catch(e => {
      dispatch(fetchOrdersOrSalesError(storableError(e)));
      throw e;
    });
};
